/**
 * Licensed Materials - Property of IBM
 * IBM Cognos Products: BI Cloud (C) Copyright IBM Corp. 2019, 2020
 * US Government Users Restricted Rights - Use, duplication or disclosure restricted by GSA ADP Schedule Contract with IBM Corp.
 */
define([
	'react',
	'prop-types',
	'@ba/ba-ui-carbon-toolkit',
	'jquery',
	'./contextMenu/ContextMenuWrapper',
	'../../widgets/livewidget/nls/StringResources',
	'../../visualizations/vipr/VIPRBundleHelper',
	'../../lib/@waca/dashboard-common/dist/lib/@ba/ba-graphics/dist/icons-js/overflow-menu--vertical_32'
], function(
	React,
	PropTypes,
	UI_Toolkit,
	$,
	ContextMenuWrapper,
	StringResources,
	VIPRBundleHelper,
	menuOverflow_24
) {

	const { Component } = React;
	const { FlexLayout, FlexItem, VSpacer, HSpacer, SVGIcon, Label, Dialog, Multilinetruncatedtext, TruncatedText } = UI_Toolkit;

	class VisualItem extends Component {

		constructor(props) {
			super(props);

			this.target = undefined;
			this._iconRef = null;
			this._rootRef = React.createRef();

			this.state = {
				item: this.props.item,
				isContextMenuOpen: false,
				openDeleteDialog: false
			};

			const { item } = this.state;

			this._renderContext = {
				icon: item.icon,
				description: item.label,
				isDisabled: false
			};
		}

		componentDidMount() {
			if ( !this._renderContext.isDisabled ) {
				this._attachCallbacksAndImages();
			}
		}

		_attachCallbacksAndImages() {
			const $node = this._findChildVisCustomVisNode();
			if ($node) {
				const { handlers } = this.props;
				handlers.componentDidMountCallback('vis-custom-container', 'visualItemLayout', $node);
			}
			this._appendIconNode();
		}

		componentWillUnmount () {
			if ( !this._renderContext.isDisabled ) {
				this._detachCallbacksAndImages();
			}
		}

		_detachCallbacksAndImages() {
			const $node = this._findChildVisCustomVisNode();
			if ($node) {
				const { handlers } = this.props;
				handlers.componentWillUnmountCallback('vis-custom-container', 'visualItemLayout', $node);
			}
			this._iconRef = null;
		}

		render () {
			const { item } = this.state;
			const className = 'vis-custom-vis' + (this._renderContext.isDisabled ? ' no-sdk' : '');

			return (
				<FlexItem
					className={this._getVisualItemCssClasses()}
					role={'group'}
				>
					<div ref={this._rootRef}>
						<FlexLayout
							fullHeight
							direction="column"
						>
							<FlexItem
								className={className}
								data-name={item.name}
							>
								{this._renderItem()}
							</FlexItem>
							<FlexItem>
								<VSpacer size={1} />
							</FlexItem>
						</FlexLayout>
						{this.renderDeleteDialog()}
					</div>
				</FlexItem>
			);
		}

		_getVisualItemCssClasses () {
			return 'vis-custom-visual-item';
		}

		_renderItem () {
			const { item } = this.state;
			let renderOptionsContent;

			if(this._canRenderMenuOption()) {
				if(this._isSchematic(item) && item.numSVGs > 1) {
					renderOptionsContent = this._renderSchematicCountWithOptions();
				} else {
					renderOptionsContent = this._renderMenuOption();
				}
			} else {
				renderOptionsContent = (<VSpacer size={1.5} />);
			}

			return (
				<FlexLayout
					fullHeight
					direction="column"
					className='visualItemLayout'
				>
					<FlexItem>
						<VSpacer size={0.5} />
					</FlexItem>
					<FlexItem className='vis-options'>
						{renderOptionsContent}
					</FlexItem>
					<FlexItem>
						{this._renderContent()}
					</FlexItem>
				</FlexLayout>
			);
		}

		_renderContent () {
			const { item } = this.state;
			const className = 'prop-item' + (this._renderContext.isDisabled ? ' is-disabled' : '');

			return (
				<FlexLayout
					fullHeight
					direction="column"
					role={'button'}
					className={className}
					data-name={item.name}
				>
					<FlexItem
						role={'group'}
					>
						{this._renderVisIcon()}
					</FlexItem>
					<FlexItem>
						{this._renderVisLabel()}
					</FlexItem>
				</FlexLayout>
			);
		}

		_canRenderMenuOption () {
			const { customVisAction } = this.props;

			return customVisAction.canManage();
		}

		_isSchematic (item) {
			const { handlers } = this.props;

			return handlers.isSchematicType(item) && !handlers.isSchematicTypePreview(item);
		}

		_renderMenuOption () {
			const { menuIcon } = this.props;
			const { item } = this.state;

			return (
				<FlexLayout
					fullHeight
					direction='row-reverse'
					className='vis-custom-menu-overflow'
				>
					<FlexItem>
						<HSpacer size={1} />
					</FlexItem>
					<FlexItem
						data-name={item.name}
						className='vis-custom-menu-overflow-anchor'
						title={StringResources.get('extVisMore')}
					>
						<SVGIcon
							size='small'
							className='svgIcon'
							verticalAlign={'initial'}
							tabIndex={0}
							iconId={menuIcon.default.id}
							onKeyUp={this._renderContextMenu.bind(this)}
							onClick={this._handleOnClick.bind(this)}
						/>
						{this._renderContextMenu()}
					</FlexItem>
				</FlexLayout>
			);
		}

		_renderSchematicCountWithOptions () {
			const { item } = this.state;
			const renderMenuOption = this._canRenderMenuOption();
			const width = renderMenuOption ? '50%' : '100%';

			return (
				<FlexLayout
					fullHeight
					direction='row'
					justifyContent='space-between'
				>
					<FlexItem width={width} grow={true} shrink={true}>
						<FlexLayout
							fullHeight
							direction='row'
							justifyContent='flex-start'
						>
							<FlexItem>
								<HSpacer size={0.5} />
							</FlexItem>
							<FlexItem>
								<div
									className='vis-svg-count-container'
									title={item.numSVGs}
									aria-label={item.numSVGs}
								>
									<Label
										className='vis-svg-count-content'
										label={item.numSVGs}/>
								</div>
							</FlexItem>
						</FlexLayout>
					</FlexItem>
					{renderMenuOption && (
						<FlexItem grow={true} shrink={true}>
							{this._renderMenuOption()}
						</FlexItem>
					)}
				</FlexLayout>
			);
		}

		_renderVisIcon () {
			const { item } = this.state;
			return (
				<FlexLayout
					fullHeight
					direction="column"
					alignItems='center'
					tabIndex={0}
					className='prop-icon'
					aria-label={item.label}
				>
					<FlexItem>
						{VIPRBundleHelper.isValidIconFileExt(item.icon) ? this._renderIconRef() : this._renderIcon()}
					</FlexItem>
				</FlexLayout>
			);
		}

		_renderIcon () {
			const className = 'svgIcon' + (this._renderContext.isDisabled ? ' is-disabled' : '');
			return (
				<SVGIcon
					iconId={this._renderContext.icon}
					height={24}
					width={24}
					className={className} />
			);
		}

		_renderIconRef () {
			this._iconRef = React.createRef();
			return (
				<div ref={this._iconRef} />
			);
		}

		_appendIconNode() {
			if (this._iconRef && this._iconRef.current) {
				const { item } = this.state;
				const { placeholderIcon } = item;

				const imgStr = '<img src="' + placeholderIcon + '" />';
				const $child = $(imgStr);

				$child.addClass('is-small');
				$child.addClass('ba-common-svgIcon');
				$child.css({
					height: '24px',
					width: '24px',
					'vertical-align': 'bottom'
				});
				this._iconRef.current.appendChild($child[0]);
			}
		}

		_renderVisLabel() {
			const className = 'vis-custom-vis-label' + (this._renderContext.isDisabled ? ' no-sdk' : '');
			const SEPARATOR = ' ';
			const containSeparator = this._renderContext.description.includes(SEPARATOR);
			return (
				<FlexLayout
					fullHeight
					direction="column"
					alignItems='center'
					aria-label={this._renderContext.description}
				>
					<FlexItem>
						{containSeparator ? (
							<Multilinetruncatedtext
								className={className}
								value={this._renderContext.description}
								numLines={2}/>
						) : (
							<TruncatedText
								className={className}
								value={this._renderContext.description}/>
						)}
					</FlexItem>
				</FlexLayout>
			);
		}

		_renderContextMenu() {
			const { item } = this.state;

			if (!this.state.isContextMenuOpen || !this.target || (this.targetName && this.targetName !== item.name)) {
				return;
			}

			return this._getContextMenuContent();
		}

		_getContextMenuContent () {
			const placement = 'bottomRight';
			const align = 'left';

			return (
				<ContextMenuWrapper
					theme={true}
					placement={placement}
					align={align}
					triggerNode={this.target}
					domNodeToAttachTo={document.body}
					onClose={this.onClose.bind(this)}
					onChange={this.onChange.bind(this)}
					contextMenuItems={this._getContextMenuItems()}/>
			);
		}

		onClose () {
			if (this.target) {
				$(this.target).removeClass('open-context-menu');
			}
			this.target = undefined;
			this.targetName = undefined;
			this.setState({
				isContextMenuOpen: false
			});
		}

		onChange (name, value) {
			if(value === 'delete') {
				this.setState({ openDeleteDialog: true });
			} else if (value === 'update') {
				const { customVisAction } = this.props;
				const { item } = this.state;
				if (customVisAction.openSelectBundleFileDialog) {
					customVisAction.openSelectBundleFileDialog(item);
				}
			}
			this.onClose();
		}

		renderDeleteDialog() {
			let dialogProps = {
				size: 'small',
				width: '20px',
				minWidth: '450px',
				maxWidth: '500px',
				clickaway: false,
				theme: 'ba-theme-default',
				startingFocusIndex: 1
			};
			return (
				<div>
					{
						this.state.openDeleteDialog && (<Dialog {...dialogProps} onClose={() => { this.setState({ openDeleteDialog: false }); }} >
							<Dialog.Header>{StringResources.get('extVisDeleteDialogTitle')}</Dialog.Header>
							<Dialog.Body>{StringResources.get('extVisDeleteDialogBody')}</Dialog.Body>
							<Dialog.Footer>
								<Dialog.Button primary label={StringResources.get('extVisCustomDelete')} onClick={() => {this.deleteCustomVisualization();}}/>
								<Dialog.Button label={StringResources.get('dlg_cancel')} onClick={() => {this.setState({ openDeleteDialog: false });}}/>
							</Dialog.Footer>
						</Dialog>)
					}
				</div>
			);
		}

		deleteCustomVisualization () {
			this.setState({ openDeleteDialog: false });
			const { onContextMenuChange } = this.props;
			const { item } = this.state;
			onContextMenuChange(item, 'delete');
		}

		_getContextMenuItems () {
			const updateItem = [{
				id: 'update',
				value: 'update',
				label: StringResources.get('extVisCustomUpdate')
			}];

			const deleteItem = [{
				id: 'delete',
				value: 'delete',
				label: StringResources.get('extVisCustomDelete')
			}];

			const sepator = {};
			return [{ items: updateItem }, sepator, { items: deleteItem }];
		}

		_handleOnClick (event) {
			if (this.state.isContextMenuOpen) {
				if (this.target) {
					$(this.target).removeClass('open-context-menu');
				}
				this.target = undefined;
				this.targetName = undefined;
				this.setState({
					isContextMenuOpen: false
				});
			} else {
				let $node = $(event.target);
				$node = $node.closest('.vis-custom-menu-overflow-anchor');
				$node.addClass('open-context-menu');
				this.target = $node[0];
				this.targetName = $node.data('name');
				this.setState({
					isContextMenuOpen: true
				});
			}
		}

		_findChildVisCustomVisNode () {
			return (this._rootRef.current) ? $(this._rootRef.current).find('.prop-item') : null;
		}
	}

	VisualItem.propTypes = {
		item: PropTypes.object.isRequired,
		iconsFeature: PropTypes.object.isRequired,
		handlers: PropTypes.shape({
			componentDidMountCallback: PropTypes.func.isRequired,
			componentWillUnmountCallback: PropTypes.func.isRequired,
			isSchematicType: PropTypes.func.isRequired,
			isSchematicTypePreview: PropTypes.func.isRequired
		}).isRequired,
		openSelectBundleFileDialog: PropTypes.func.isRequired,
		onContextMenuChange: PropTypes.func,
		customVisAction: PropTypes.object.isRequired,
		menuIcon: PropTypes.object
	};

	VisualItem.defaultProps = {
		onContextMenuChange: () => {},
		openSelectBundleFileDialog: () => {},
		menuIcon: menuOverflow_24
	};

	return VisualItem;
});
